import type { RefCallback } from 'react';
import { useCallback, useRef, useState } from 'react';

export type ElementSize = {
  height: number;
  width: number;
  scrollHeight: number;
  scrollWidth: number;
};

const MUTATION_OBSERVER_CONFIG = {
  subtree: true,
  childList: true
};

const DEFAULT_ELEMENT_SIZE: ElementSize = {
  height: 0,
  width: 0,
  scrollHeight: 0,
  scrollWidth: 0
};

function isSizeEqual(oldSize: ElementSize, newSize: ElementSize) {
  return (
    oldSize.width === newSize.width &&
    oldSize.height === newSize.height &&
    oldSize.scrollWidth === newSize.scrollWidth &&
    oldSize.scrollHeight === newSize.scrollHeight
  );
}

export type SizeChangeCallback = (newSize: ElementSize) => void;

export function useElementSize(
  onChange: SizeChangeCallback = () => {}
): [RefCallback<HTMLElement>, ElementSize] {
  const [elementSize, setElementSize] = useState(DEFAULT_ELEMENT_SIZE);
  const elementSizeRef = useRef(DEFAULT_ELEMENT_SIZE);

  const refreshSize = () => {
    if (!targetRef.current) {
      return;
    }

    const newSize = {
      height: targetRef.current.clientHeight,
      width: targetRef.current.clientWidth,
      scrollHeight: targetRef.current.scrollHeight,
      scrollWidth: targetRef.current.scrollWidth
    };

    if (!isSizeEqual(elementSize, newSize)) {
      setElementSize(newSize);
      Object.assign(elementSizeRef.current, newSize);
      onChange(newSize);
    }
  };

  const targetRef = useRef<HTMLElement | null>(null);

  const resizeObserverRef = useRef(
    new ResizeObserver((entries) => {
      for (const _ of entries) {
        refreshSize();
      }
    })
  );

  const mutationObserverRef = useRef(
    new MutationObserver((entries) => {
      entries.forEach(() => {
        requestAnimationFrame(() => {
          refreshSize();
        });
      });
    })
  );

  const elementCallbackRef = useCallback((elemNode: HTMLElement) => {
    if (elemNode) {
      targetRef.current = elemNode;

      refreshSize();

      resizeObserverRef.current.disconnect();
      mutationObserverRef.current.disconnect();
      resizeObserverRef.current.observe(elemNode);
      mutationObserverRef.current.observe(elemNode, MUTATION_OBSERVER_CONFIG);
    } else {
      resizeObserverRef.current.disconnect();
      mutationObserverRef.current.disconnect();

      targetRef.current = null;
    }
  }, []);

  return [elementCallbackRef, elementSize];
}
